import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  NavigationGuardWithThis,
  RouteLocationRaw,
  RouteLocationNormalized
} from 'vue-router'
import { isHttpError } from '@/api/fetchClient'
import { useUserStore } from '@/stores/userStore'
import { onboardingControllerHasPaid as hasPaid } from '@/gen/clients/onboardingControllerHasPaid'
import { Routes } from './routes'

type UserStore = ReturnType<typeof useUserStore>

async function resolveStore(to: RouteLocationNormalized): Promise<UserStore | RouteLocationRaw> {
  const store = useUserStore()

  if (!store.initialized) {
    try {
      const result = await store.initialize()

      // Handle routing based on authentication result
      if (result.needsOnboarding) {
        // User needs onboarding
        return { name: Routes.ONBOARDING }
      } else if (result.needsPayment) {
        // User needs to complete payment
        return { name: Routes.CHECKOUT }
      }
    } catch {
      return { name: Routes.LOGIN, query: { redirect: to.fullPath } }
    }
  }

  return store
}

function isRedirect(x: UserStore | RouteLocationRaw): x is RouteLocationRaw {
  return !!x && typeof x === 'object' && 'name' in x
}

function roleGuard(...roles: (keyof UserStore)[]): NavigationGuardWithThis<unknown> {
  return async to => {
    const res = await resolveStore(to)
    if (isRedirect(res)) return res
    const store = res

    return roles.some(k => Boolean(store[k])) ? true : { name: Routes.NOT_AUTHORIZED }
  }
}

export const requireAuth: NavigationGuardWithThis<unknown> = async to => {
  const res = await resolveStore(to)
  if (isRedirect(res)) return res
  const store = res

  if (!store.loggedIn) {
    return { name: Routes.LOGIN, query: { redirect: to.fullPath } }
  }
  if (store.user.deactivated) {
    return { name: Routes.NOT_AUTHORIZED }
  }
  return true
}

export const requireSales = roleGuard('isSales', 'isAdmin')
export const requireAdmin = roleGuard('isAdmin')
export const requireOrgaAdmin = roleGuard('isOrgaAdmin', 'isAdmin')
export const requireSuperOrgaAdmin = roleGuard('isSuperOrgaAdmin', 'isAdmin')
export const requireBetaAccess = roleGuard('isBetaAccess')

function fallbackToLogin(): RouteLocationRaw {
  return { name: Routes.LOGIN }
}

const CHUNK_RECOVERY_KEY = 'nr-chunk-recovery'

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Loading chunk')
  )
}

function redirectAfterPayment(store: UserStore): RouteLocationRaw {
  return store.isUser ? { name: Routes.HOME } : { name: Routes.ONBOARDING }
}

export const requirePreCheckout: NavigationGuardWithThis<unknown> = async to => {
  const resolved = await resolveStore(to)
  if (isRedirect(resolved)) return resolved
  const store = resolved

  if (!store.accessToken) return fallbackToLogin()

  const managedOrganisationUuid =
    typeof to.query.organisationUuid === 'string' ? to.query.organisationUuid.trim() : ''
  if (managedOrganisationUuid.length > 0) {
    return store.isSuperOrgaAdmin ? true : { name: Routes.NOT_AUTHORIZED }
  }

  const org = store.user.organisation
  const accountType = org.accountType
  const hasStripeCustomer = store.subscriptionStatus?.hasStripeCustomer ?? !!org.stripeCustomerID

  // SELF_SERVICE accounts can always reach checkout:
  // - no Stripe customer yet -> first purchase
  // - existing Stripe customer -> add/change seats
  if (accountType === 'SELF_SERVICE') {
    return true
  }

  // Enterprise and Free accounts never use Stripe checkout.
  if (accountType === 'ENTERPRISE' || accountType === 'FREE') {
    return redirectAfterPayment(store)
  }

  // If we already know this user is paid and attached to Stripe, skip checkout.
  if (store.hasTierAccess && hasStripeCustomer) {
    return redirectAfterPayment(store)
  }

  // For non-admin users the org snapshot may miss accountType/customer fields.
  // Fallback to backend payment status instead of assuming checkout is needed.
  try {
    const status = await hasPaid()
    return status.hasPaid ? redirectAfterPayment(store) : true
  } catch (error) {
    if (isHttpError(error) && error.response.status === 402) {
      return true
    }
    return fallbackToLogin()
  }
}

export const redirectIfLoggedIn: NavigationGuardWithThis<unknown> = async () => {
  const store = useUserStore()
  return store.loggedIn ? { name: Routes.HOME } : true
}

export const requireOnboarding: NavigationGuardWithThis<unknown> = async to => {
  const resolved = await resolveStore(to)
  if (isRedirect(resolved)) return resolved
  const store = resolved
  return store.isUser || !store.accessToken ? { name: Routes.HOME } : true
}

export const handleLoginRedirect: NavigationGuardWithThis<unknown> = async to => {
  const store = useUserStore()

  // If already initialized and logged in, redirect away from login
  if (store.initialized && store.loggedIn) {
    const redirectPath = to.query.redirect as string
    if (redirectPath && redirectPath !== '/' && !redirectPath.includes('/login')) {
      return redirectPath
    } else {
      return { name: Routes.HOME }
    }
  }

  // If not initialized but has access token, try to initialize
  if (!store.initialized && store.accessToken) {
    try {
      const result = await store.initialize()

      if (result.userAuthenticated) {
        const redirectPath = to.query.redirect as string
        if (redirectPath && redirectPath !== '/' && !redirectPath.includes('/login')) {
          return redirectPath
        } else {
          return { name: Routes.HOME }
        }
      } else if (result.needsOnboarding) {
        return { name: Routes.ONBOARDING }
      } else if (result.needsPayment) {
        return { name: Routes.CHECKOUT }
      }
    } catch {
      // If initialization fails, allow access to login page
      return true
    }
  }

  // Allow access to login page (no token or not logged in)
  return true
}

const FEATURE_NEWSBOX = import.meta.env.VITE_FEATURE_NEWSBOX === 'true'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: Routes.LOGIN,
    component: () => import('../views/Login/LoginView.vue'),
    beforeEnter: handleLoginRedirect
  },
  {
    path: '/start',
    name: Routes.START,
    component: () => import('../views/Login/StartView.vue'),
    beforeEnter: redirectIfLoggedIn
  },
  {
    path: '/register',
    name: Routes.REGISTER,
    component: () => import('../views/Login/RegisterView.vue'),
    beforeEnter: redirectIfLoggedIn
  },
  {
    path: '/redirect-failure',
    name: Routes.REDIRECT_FAILURE,
    component: () => import('../views/Login/RedirectFailureView.vue')
  },
  {
    path: '/onboarding',
    name: Routes.ONBOARDING,
    component: () => import('../views/Login/OnboardingView.vue'),
    beforeEnter: requireOnboarding
  },
  {
    path: '/logout',
    name: Routes.LOGOUT,
    component: () => import('../views/Login/LogoutView.vue')
  },
  { path: '/tos', name: Routes.TOS, component: () => import('../views/privacy/TosView.vue') },
  {
    path: '/privacy',
    name: Routes.PRIVACY,
    component: () => import('../views/privacy/PrivacyView.vue')
  },
  {
    path: '/dpa',
    name: Routes.DPA,
    component: () => import('../views/privacy/DPAView.vue')
  },
  {
    path: '/anlage1',
    name: Routes.ANLAGE1,
    component: () => import('../views/privacy/AnlageOne.vue')
  },
  {
    path: '/anlage2',
    name: Routes.ANLAGE2,
    component: () => import('../views/privacy/AnlageTwo.vue')
  },
  {
    path: '/imprint',
    name: Routes.IMPRINT,
    component: () => import('../views/privacy/ImprintView.vue')
  },

  {
    path: '/pre-checkout',
    name: Routes.PRE_CHECKOUT,
    redirect: { name: Routes.CHECKOUT }
  },
  {
    path: '/checkout',
    name: Routes.CHECKOUT,
    beforeEnter: requirePreCheckout,
    component: () => import('../views/Checkout/CheckoutPage.vue')
  },

  {
    path: '/tutorial',
    name: Routes.TUTORIAL,
    component: () => import('../views/TutorialView.vue')
  },
  {
    path: '/',
    beforeEnter: requireAuth,
    children: [
      {
        path: '/',
        name: Routes.HOME,
        component: () => import('../views/HomeView.vue')
      },
      {
        path: '/editor',
        name: Routes.EDITOR,
        component: () => import('../views/CookingPot/CookingPotView.vue')
      },
      {
        path: '/cookingpot',
        name: Routes.COOKINGPOT,
        redirect: { name: Routes.EDITOR }
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            name: Routes.PROFILE,
            component: () => import('../views/Profile/ProfileView.vue')
          },
          {
            path: 'productivity',
            name: Routes.PROFILE_PRODUCTIVITY,
            component: () => import('../views/Profile/ProductivityView.vue')
          },
          {
            path: 'organisation',
            name: Routes.PROFILE_ORGANISATION,
            beforeEnter: requireOrgaAdmin,
            component: () => import('../views/Profile/OrganisationView.vue')
          },
          {
            path: 'super-organisation',
            name: Routes.PROFILE_SUPER_ORGANISATION,
            beforeEnter: requireSuperOrgaAdmin,
            component: () => import('../views/Profile/SuperOrganisationView.vue')
          },
          {
            path: 'format-builder',
            name: Routes.PROFILE_FORMAT_BUILDER,
            beforeEnter: requireOrgaAdmin,
            component: () => import('../views/Profile/OrgaFormatBuilderView.vue')
          },
          {
            path: 'language-settings',
            name: Routes.PROFILE_LANGUAGE_SETTINGS,
            beforeEnter: requireOrgaAdmin,
            component: () => import('../views/Profile/LanguageSettingsView.vue')
          },
          {
            path: 'style-settings',
            name: Routes.PROFILE_STYLE_SETTINGS,
            beforeEnter: requireOrgaAdmin,
            component: () => import('../views/Profile/StyleSettingsView.vue')
          },
          {
            path: 'change-password',
            name: Routes.PROFILE_PASSWORD,
            component: () => import('../views/Profile/ChangePasswordView.vue')
          }
        ]
      },
      {
        path: 'rewards',
        name: Routes.REWARDS,
        component: () => import('../views/WorkSessionArchiveView.vue')
      },
      {
        path: 'content',
        name: Routes.CONTENT,
        component: () => import('../views/ContentView.vue')
      },
      { path: 'live-content', redirect: '/content' },
      {
        path: 'assistant',
        name: Routes.ASSISTANT,
        beforeEnter: requireBetaAccess,
        component: () => import('../views/Assistant/AssistantView.vue')
      },
      {
        path: 'research-chat',
        name: Routes.RESEARCH_CHAT,
        beforeEnter: requireBetaAccess,
        component: () => import('../views/ResearchChat/ResearchChatView.vue')
      },
      {
        path: 'image-studio',
        name: Routes.IMAGE_STUDIO,
        beforeEnter: requireBetaAccess,
        component: () => import('../views/ImageStudio/ImageStudioView.vue')
      },
      {
        path: 'agent',
        name: Routes.HEARTBEAT,
        beforeEnter: requireBetaAccess,
        component: () => import('../views/Heartbeat/HeartbeatView.vue')
      },
      {
        path: 'heartbeat',
        redirect: { name: Routes.HEARTBEAT }
      },
      {
        path: 'hearbeat',
        redirect: { name: Routes.HEARTBEAT }
      },
      ...(FEATURE_NEWSBOX
        ? [
            {
              path: 'newsbox',
              name: Routes.NEWSBOX,
              beforeEnter: requireBetaAccess,
              component: () => import('../views/NewsboxView.vue'),
              meta: { hideNavbar: true }
            }
          ]
        : [])
    ]
  },

  {
    path: '/prompts',
    beforeEnter: requireSales,
    children: [
      { path: '', name: Routes.PROMPTS, component: () => import('../views/PromptView.vue') },
      {
        path: 'prompt-editor/:uuid?',
        name: Routes.PROMPT_EDITOR,
        component: () => import('../views/PromptEditor.vue')
      },
      {
        path: 'prompt-creation',
        name: Routes.PROMPT_CREATION,
        component: () => import('../views/PromptCreation.vue')
      }
    ]
  },

  {
    path: '/ai/admin',
    beforeEnter: requireSales,
    children: [
      {
        path: 'users',
        name: Routes.ADMIN_USERS,
        component: () => import('../views/OrganisationDetails/UserTableView.vue')
      },
      {
        path: 'users/create',
        name: Routes.ADMIN_USER_CREATE,
        component: () => import('../views/OrganisationDetails/AdminUserCreateView.vue')
      },
      {
        path: 'users/:uuid',
        name: Routes.ADMIN_USER_DETAILS,
        component: () => import('../views/OrganisationDetails/AdminUserDetailsView.vue')
      },
      {
        path: 'organisations',
        name: Routes.ADMIN_ORGANISATIONS,
        component: () => import('../views/OrganisationDetails/OrganisationTableView.vue')
      },
      {
        path: 'organisations/create',
        name: Routes.ADMIN_ORGANISATION_CREATE,
        component: () => import('../views/OrganisationDetails/OrganisationCreateView.vue')
      },
      {
        path: 'customizations/create',
        name: Routes.ADMIN_CUSTOMIZATION_CREATE,
        component: () => import('../views/OrganisationDetails/CustomizationCreateView.vue')
      },
      {
        path: 'organisations/:uuid',
        name: Routes.ADMIN_ORGANISATION_DETAILS,
        component: () => import('../views/OrganisationDetails/AdminOrganisationDetailsView.vue')
      },
      {
        path: 'customizations',
        name: Routes.ADMIN_CUSTOMIZATIONS,
        component: () => import('../views/OrganisationDetails/CustomizationTableView.vue')
      },
      {
        path: 'customizations/:uuid',
        name: Routes.ADMIN_CUSTOMIZATION_DETAILS,
        component: () => import('../views/OrganisationDetails/CustomizationDetailView.vue')
      },
      {
        path: 'scopes',
        name: Routes.ADMIN_SCOPES,
        component: () => import('../views/ScopeManagement/ScopeTableView.vue')
      },
      {
        path: 'scopes/create',
        name: Routes.ADMIN_SCOPE_CREATE,
        component: () => import('../views/ScopeManagement/ScopeCreateView.vue')
      },
      {
        path: 'scopes/:uuid',
        name: Routes.ADMIN_SCOPE_DETAILS,
        component: () => import('../views/ScopeManagement/ScopeDetailsView.vue')
      },
      {
        path: 'live-content-sources',
        name: Routes.ADMIN_LIVE_CONTENT_SOURCES,
        component: () => import('../views/LiveContentManagement/LiveContentSourceTableView.vue')
      },
      {
        path: 'live-content-sources/create',
        name: Routes.ADMIN_LIVE_CONTENT_SOURCE_CREATE,
        component: () => import('../views/LiveContentManagement/LiveContentSourceCreateView.vue')
      },
      {
        path: 'live-content-sources/:uuid',
        name: Routes.ADMIN_LIVE_CONTENT_SOURCE_DETAILS,
        component: () => import('../views/LiveContentManagement/LiveContentSourceDetailsView.vue')
      },
      {
        path: 'statistics',
        name: Routes.ADMIN_STATISTICS,
        component: () => import('../views/OrganisationDetails/AdminStatistics.vue')
      },
      {
        path: 'dashboard',
        name: Routes.ADMIN_DASHBOARD,
        component: () => import('../views/AdminSales/AdminSalesDashboard.vue')
      },
      {
        path: 'format-builder',
        name: Routes.ADMIN_FORMAT_BUILDER,
        component: () => import('../feature/FormatBuilder/FormatBuilder.vue')
      },
      {
        path: 'format-builder/:customizationUuid',
        name: Routes.ADMIN_FORMAT_BUILDER_CUSTOMIZATION,
        component: () => import('../feature/FormatBuilder/FormatBuilder.vue')
      },
      {
        path: 'style-settings/:customizationUuid',
        name: Routes.ADMIN_STYLE_SETTINGS,
        component: () => import('../views/Profile/StyleSettingsView.vue'),
        props: { mode: 'admin' }
      },
      {
        path: 'language-settings/:customizationUuid',
        name: Routes.ADMIN_LANGUAGE_SETTINGS,
        component: () => import('../views/Profile/LanguageSettingsView.vue'),
        props: { mode: 'admin' }
      },
      {
        path: 'onboarding-flows',
        name: Routes.ADMIN_ONBOARDING_FLOWS,
        beforeEnter: requireAdmin,
        component: () => import('../views/OnboardingFlows/OnboardingFlowsAdminView.vue')
      },
      {
        path: 'onboarding-flows/create',
        name: Routes.ADMIN_ONBOARDING_FLOW_CREATE,
        beforeEnter: requireAdmin,
        component: () => import('../views/OnboardingFlows/OnboardingFlowCreateView.vue')
      },
      {
        path: 'onboarding-flows/:flowId',
        name: Routes.ADMIN_ONBOARDING_FLOW_DETAILS,
        beforeEnter: requireAdmin,
        component: () => import('../views/OnboardingFlows/OnboardingFlowDetailsView.vue')
      },
      {
        path: 'user-styles',
        name: Routes.ADMIN_USER_STYLES,
        beforeEnter: requireAdmin,
        component: () => import('../views/UserStyles/UserStylesAdminView.vue')
      },
      {
        path: 'user-styles/:userUuid',
        name: Routes.ADMIN_USER_STYLE_DETAILS,
        beforeEnter: requireAdmin,
        component: () => import('../views/UserStyles/UserStyleDetailsView.vue')
      },
      {
        path: 'tprompts',
        name: Routes.ADMIN_TPROMPTS,
        beforeEnter: requireAdmin,
        component: () => import('../views/ModelManagement/ModelTableView.vue')
      },
      {
        path: 'feature-templates',
        redirect: { name: Routes.ADMIN_TPROMPTS }
      },
      {
        path: 'models',
        redirect: { name: Routes.ADMIN_TPROMPTS }
      },
      {
        path: 'model-catalog',
        name: Routes.ADMIN_MODEL_CATALOG,
        beforeEnter: requireAdmin,
        component: () => import('../views/ModelCatalog/ModelCatalogTableView.vue')
      },
      {
        path: 'model-catalog/:uuid',
        name: Routes.ADMIN_MODEL_CATALOG_DETAILS,
        beforeEnter: requireAdmin,
        component: () => import('../views/ModelCatalog/ModelCatalogDetailsView.vue')
      },
      {
        path: 'api-restrictions',
        name: Routes.ADMIN_API_RESTRICTIONS,
        beforeEnter: requireAdmin,
        component: () => import('../views/PathRestrictionManagement/PathRestrictionTableView.vue')
      },
      {
        path: 'api-restrictions/create',
        name: Routes.ADMIN_API_RESTRICTION_CREATE,
        beforeEnter: requireAdmin,
        component: () => import('../views/PathRestrictionManagement/PathRestrictionCreateView.vue')
      },
      {
        path: 'api-restrictions/:uuid',
        name: Routes.ADMIN_API_RESTRICTION_DETAILS,
        beforeEnter: requireAdmin,
        component: () => import('../views/PathRestrictionManagement/PathRestrictionDetailsView.vue')
      },
      {
        path: 'path-restrictions',
        redirect: { name: Routes.ADMIN_API_RESTRICTIONS }
      },
      {
        path: 'path-restrictions/create',
        redirect: { name: Routes.ADMIN_API_RESTRICTION_CREATE }
      },
      {
        path: 'path-restrictions/:uuid',
        redirect: to => ({
          name: Routes.ADMIN_API_RESTRICTION_DETAILS,
          params: { uuid: String(to.params.uuid) }
        })
      },
      {
        path: 'usage-restrictions',
        name: Routes.ADMIN_USAGE_RESTRICTIONS,
        beforeEnter: requireAdmin,
        component: () => import('../views/UsageRestrictionManagement/UsageRestrictionTableView.vue')
      },
      {
        path: 'usage-restrictions/create',
        name: Routes.ADMIN_USAGE_RESTRICTION_CREATE,
        beforeEnter: requireAdmin,
        component: () =>
          import('../views/UsageRestrictionManagement/UsageRestrictionCreateView.vue')
      },
      {
        path: 'usage-restrictions/:uuid',
        name: Routes.ADMIN_USAGE_RESTRICTION_DETAILS,
        beforeEnter: requireAdmin,
        component: () =>
          import('../views/UsageRestrictionManagement/UsageRestrictionDetailsView.vue')
      },
      {
        path: 'research-chat-analytics',
        name: Routes.ADMIN_RESEARCH_CHAT_ANALYTICS,
        beforeEnter: requireAdmin,
        component: () => import('../views/ResearchChatStatistics/ResearchChatStatisticsView.vue')
      },
      {
        path: 'research-chat-statistics',
        redirect: { name: Routes.ADMIN_RESEARCH_CHAT_ANALYTICS }
      },
      {
        path: 'affiliates',
        name: Routes.ADMIN_AFFILIATES,
        beforeEnter: requireSales,
        component: () => import('../views/AffiliateManagement/AffiliateTableView.vue')
      },
      {
        path: 'affiliates/:uuid',
        name: Routes.ADMIN_AFFILIATE_DETAILS,
        beforeEnter: requireSales,
        component: () => import('../views/AffiliateManagement/AffiliateDetailView.vue')
      }
    ]
  },

  {
    path: '/chief-editor',
    name: Routes.CHIEF_EDITOR,
    beforeEnter: requireSales,
    component: () => import('../views/ChiefEditor/ChiefEditorView.vue')
  },

  {
    path: '/chief-editor/user/:userUuid',
    name: Routes.USER_CHIEF_EDITOR,
    beforeEnter: requireSales,
    component: () => import('../views/ChiefEditor/UserChiefEditorView.vue')
  },

  {
    path: '/audio-batch',
    name: Routes.AUDIO_BATCH,
    beforeEnter: requireAdmin,
    component: () => import('../views/AudioBatchView.vue')
  },

  {
    path: '/not-authorized',
    name: Routes.NOT_AUTHORIZED,
    component: () => import('../views/NotAuthorizedView.vue')
  },

  {
    path: '/:pathMatch(.*)*',
    name: Routes.NOT_FOUND,
    beforeEnter: requireAuth,
    component: () => import('../views/NotFoundView.vue')
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeResolve(to => {
  const store = useUserStore()

  if (to.name === Routes.LOGOUT) return

  if (!store.initialized) return

  if (store.mustChangePassword && to.name !== Routes.PROFILE_PASSWORD) {
    return { name: Routes.PROFILE_PASSWORD }
  }

  if (store.mustChangePassword) return

  const isUnfinishedUser = store.isUnfinished && !store.isUser
  const isAllowedForUnfinished = to.name === Routes.ONBOARDING || to.name === Routes.LOGOUT
  if (isUnfinishedUser && !isAllowedForUnfinished) {
    return { name: Routes.ONBOARDING }
  }
})

router.afterEach(() => {
  sessionStorage.removeItem(CHUNK_RECOVERY_KEY)
})

router.onError((error, to) => {
  if (!isChunkLoadError(error)) return

  const targetPath =
    to?.fullPath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`
  const lastRecoveryPath = sessionStorage.getItem(CHUNK_RECOVERY_KEY)

  // Only refresh once per path to avoid navigation loops if the problem persists.
  if (lastRecoveryPath === targetPath) {
    sessionStorage.removeItem(CHUNK_RECOVERY_KEY)
    return
  }

  sessionStorage.setItem(CHUNK_RECOVERY_KEY, targetPath)
  window.location.assign(targetPath)
})
